import React from 'react'
import { Col } from 'antd'

import { RadioBtn } from '@edulastic/common'
import { test as testConstants } from '@edulastic/constants'

import { SHOW_TEXT_TO_SPEECH_FOR_PASSAGES } from '@edulastic/constants/const/test'
import {
  Description,
  RadioWrapper,
  StyledAccommodationSettingKeyTitle,
  StyledAccomodationSettingContainer,
  StyledRadioGroup,
} from '../styled'
import { StyledRow } from '../../../../../../AssignTest/components/SimpleOptions/styled'
import PassageTTSSettingCheckbox from '../../../../../../Common/PassageTTSSettingCheckbox'
import {
  getTtsDescription,
  getSpeechToTextTooltipByButtonId,
} from '../../../../../../utils/accommodations'
import { Tooltip } from '../../../../../../../common/utils/helpers'

const { accommodationsSettings } = testConstants

const { textToSpeech, textToSpeechForPassages } = accommodationsSettings

const AccommodationSettingRow = ({
  accommodationSetting,
  disabled,
  premium,
  updateTestData,
  isNestedSetting,
  i18translate,
}) => {
  const descriptionId = `${accommodationSetting.key}-description`

  const renderRadioButtonWithTooltip = (radioButtonProps, children) => {
    const { id } = radioButtonProps
    const tooltipText =
      !disabled && getSpeechToTextTooltipByButtonId(id, i18translate)
    const tooltipDescriptionId = `${id}-tooltip-description`

    return (
      <>
        <Tooltip title={tooltipText} placement="topLeft">
          <RadioBtn
            {...radioButtonProps}
            aria-describedby={tooltipText ? tooltipDescriptionId : undefined}
          >
            {children}
          </RadioBtn>
        </Tooltip>
        {/* Visually hidden description for screen readers */}
        {tooltipText && (
          <span
            id={tooltipDescriptionId}
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {tooltipText}
          </span>
        )}
      </>
    )
  }

  return (
    <StyledRow
      key={accommodationSetting.key}
      align="middle"
      borderBottom={isNestedSetting && 'none'}
      padding={
        isNestedSetting ? '12px 0px 0px 0px !important' : '12px 10px !important'
      }
    >
      <Col xs={24} sm={6}>
        <StyledAccommodationSettingKeyTitle
          isNestedSetting={isNestedSetting}
          textTransform={isNestedSetting ? 'none' : 'uppercase'}
          id={`${accommodationSetting.key}-label`}
        >
          {accommodationSetting.title}
        </StyledAccommodationSettingKeyTitle>
      </Col>
      <Col xs={24} sm={18}>
        <StyledRadioGroup
          disabled={disabled || !premium}
          onChange={(e) => {
            updateTestData(accommodationSetting.key)(e.target.value)
          }}
          value={accommodationSetting.value}
          flex="0 0 calc(100% / 3)"
          isHorizontal
          name={`${accommodationSetting.key}-radio-group`}
          aria-labelledby={`${accommodationSetting.key}-label`}
          aria-describedby={descriptionId}
        >
          {renderRadioButtonWithTooltip(
            {
              value:
                accommodationSetting.doNotAllowUndefined &&
                !accommodationSetting.hasEnableOption
                  ? true
                  : undefined,
              'data-cy': `${accommodationSetting.key}-student-level`,
              id: `${accommodationSetting.key}-student-level`,
              style: {
                visibility: accommodationSetting.hasStudentLevelOption
                  ? 'visible'
                  : 'hidden',
              },
              tabIndex: accommodationSetting.hasStudentLevelOption
                ? undefined
                : -1,
              'aria-hidden': !accommodationSetting.hasStudentLevelOption,
            },
            'ENABLE STUDENT LEVEL'
          )}

          {renderRadioButtonWithTooltip(
            {
              'data-cy': `${accommodationSetting.key}-enable`,
              value: true,
              id: `${accommodationSetting.key}-enable`,
              style: {
                visibility: accommodationSetting.hasEnableOption
                  ? 'visible'
                  : 'hidden',
              },
              tabIndex: accommodationSetting.hasEnableOption ? undefined : -1,
              'aria-hidden': !accommodationSetting.hasEnableOption,
            },
            'ENABLE ALL'
          )}

          {renderRadioButtonWithTooltip(
            {
              'data-cy': `${accommodationSetting.key}-disable`,
              value: false,
              id: `${accommodationSetting.key}-disable`,
            },
            'DISABLE ALL'
          )}
        </StyledRadioGroup>
      </Col>

      {!accommodationSetting.hideDescriptionInTestSettings && (
        <Col span={24}>
          <Description id={descriptionId}>
            {accommodationSetting.description}
          </Description>
        </Col>
      )}
    </StyledRow>
  )
}

const Accommodations = ({
  disabled,
  updateTestData,
  premium,
  accommodationsData,
  i18translate,
}) => {
  return (
    <RadioWrapper
      disabled={disabled}
      style={{
        marginTop: '20px',
        marginBottom: 0,
        flexDirection: 'row',
      }}
    >
      {Object.keys(accommodationsData).map((key) => {
        const { settings, title = '', description = '' } = accommodationsData[
          key
        ]

        const ttsValue = accommodationsData[key].settings.find(
          (s) => s.key === textToSpeech.key
        )?.value

        const featureDescription =
          key === textToSpeech.key
            ? getTtsDescription(
                ttsValue,
                accommodationsData[key].settings.find(
                  (s) => s.key === textToSpeechForPassages.key
                )?.value
              )
            : description

        return (
          <React.Fragment key={key}>
            {settings.length > 1 ? (
              <StyledAccomodationSettingContainer>
                <StyledAccommodationSettingKeyTitle>
                  {title}
                </StyledAccommodationSettingKeyTitle>
                {settings.map((setting) => {
                  if (setting.key === SHOW_TEXT_TO_SPEECH_FOR_PASSAGES) {
                    return (
                      <StyledRow
                        borderBottom="none"
                        padding="8px 0px !important"
                        mt="16px"
                      >
                        {[true, undefined].includes(ttsValue) && (
                          <>
                            <Col span={ttsValue === true ? 12 : 6} />
                            <Col span={ttsValue === true ? 12 : 18}>
                              <PassageTTSSettingCheckbox
                                key={setting.key}
                                updateTestData={updateTestData}
                                checked={setting.value}
                                disabled={disabled}
                                setting={setting}
                                accommodationsData={accommodationsData}
                                premium={premium}
                              />
                            </Col>
                          </>
                        )}
                      </StyledRow>
                    )
                  }
                  return (
                    <AccommodationSettingRow
                      key={setting.key}
                      accommodationSetting={setting}
                      disabled={disabled}
                      premium={premium}
                      updateTestData={updateTestData}
                      isNestedSetting
                      i18translate={i18translate}
                    />
                  )
                })}
                {/* showTextToSpeech, showTtsForPassages */}
                {description && (
                  <Description marginTop="0px">
                    {featureDescription}
                  </Description>
                )}
              </StyledAccomodationSettingContainer>
            ) : (
              <AccommodationSettingRow
                key={settings[0].key}
                accommodationSetting={settings[0]}
                disabled={disabled}
                premium={premium}
                updateTestData={updateTestData}
                i18translate={i18translate}
              />
            )}
          </React.Fragment>
        )
      })}
    </RadioWrapper>
  )
}

export default Accommodations
